import React from "react";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isUpdating: boolean;
  error: string | null;
  orderTotal: number;
  paymentMethod?: string;
  isRTL?: boolean;
  t: (key: string) => string;
  formatCurrency: (value: number) => string;
  paymentMethodIcons: Record<string, string>;
  getPaymentMethodText: (method: string) => string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isUpdating,
  error,
  orderTotal,
  paymentMethod,
  isRTL = false,
  t,
  formatCurrency,
  paymentMethodIcons,
  getPaymentMethodText,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl ${
          isRTL ? "text-right" : ""
        }`}
      >
        <div
          className={`flex items-center justify-between mb-6 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <h3 className="text-xl font-bold text-gray-800">
            {t("order.markAsPaid")}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            aria-label={t("common.close")}
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="mb-6">
          <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-xl mb-4">
            <div
              className={`flex items-center gap-3 mb-2 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <span className="text-yellow-600 text-lg">⚠️</span>
              <span className="font-medium text-yellow-800">
                {t("order.importantNotice")}
              </span>
            </div>
            <p className="text-sm text-yellow-700">
              {t("order.paymentWarning")}
            </p>
          </div>

          <div className="space-y-4">
            <div
              className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <span className="text-gray-600">{t("order.orderTotal")}</span>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(orderTotal)}
              </span>
            </div>

            <div
              className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <span className="text-gray-600">
                {t("order.paymentMethod")}
              </span>
              <span className="flex items-center gap-2 font-medium">
                <span>
                  {paymentMethodIcons[paymentMethod || "cash"]}
                </span>
                {paymentMethod ? getPaymentMethodText(paymentMethod) : ""}
              </span>
            </div>
          </div>
        </div>

        <div
          className={`flex justify-end gap-3 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-200 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={isUpdating}
            className="px-5 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center gap-2"
          >
            {isUpdating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {t("common.processing")}
              </>
            ) : (
              <>
                <span>✓</span>
                {t("order.markAsPaid")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;